/* ==========================================================================
   Kopstorm Help Center - kopstorm-help-center.js
   Anavrin / byanavrin.com, 23 Sep 2026

   Live search over questions, answers and hidden keywords (every word must
   match, accents ignored), highlight, topic counts, scroll spy on the topic
   list, ?q= and #question deep links, helpful vote.

   Events for Shopify customer events (custom pixel), all via
   Shopify.analytics.publish when available:
     kopstorm_help_search   { query, results }   debounced, 3+ characters
     kopstorm_help_vote     { question, helpful }
   Zero result searches are the list of questions to add next.
   ========================================================================== */

(function () {
  if (customElements.get('kopstorm-help-center')) return;

  var norm = function (str) {
    return (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  /* Light stemming so battery finds batteries and glues finds glue */
  var stem = function (t) {
    return t.length > 4 ? t.replace(/(ies|es|s|y)$/, '') : t;
  };

  var escapeRe = function (str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  var publish = function (name, detail) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, detail);
      }
    } catch (e) { /* analytics is optional */ }
    document.dispatchEvent(new CustomEvent('kopstorm:' + name, { detail: detail }));
  };

  class KopstormHelpCenter extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.input = this.querySelector('[data-khc-input]');
      this.clearBtn = this.querySelector('[data-khc-clear]');
      this.status = this.querySelector('[data-khc-status]');
      this.empty = this.querySelector('[data-khc-empty]');
      this.emptyMail = this.querySelector('[data-khc-empty-mail]');
      this.groups = Array.from(this.querySelectorAll('[data-khc-group]'));
      this.navLinks = Array.from(this.querySelectorAll('[data-khc-navlink]'));
      this.sheet = this.querySelector('[data-khc-sheet]');
      this.current = this.querySelector('[data-khc-current]');
      this.currentDefault = this.current ? this.current.textContent : '';
      this.chips = Array.from(this.querySelectorAll('[data-khc-suggest]'));

      this.items = Array.from(this.querySelectorAll('[data-khc-item]')).map(function (el) {
        var q = el.querySelector('[data-khc-q]');
        var a = el.querySelector('[data-khc-a]');
        return {
          el: el,
          group: el.closest('[data-khc-group]'),
          details: el.querySelector('details'),
          q: q,
          a: a,
          qHtml: q ? q.innerHTML : '',
          aHtml: a ? a.innerHTML : '',
          text: norm((q ? q.textContent : '') + ' ' + (a ? a.textContent : '') + ' ' + (el.getAttribute('data-khc-keywords') || ''))
        };
      });

      this.fillCounts();
      this.bind();
      this.spy();
      this.readUrl();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    fillCounts() {
      this.navLinks.forEach(function (link) {
        var id = link.getAttribute('data-khc-navlink');
        var group = document.getElementById(id);
        var count = link.querySelector('[data-khc-count]');
        if (group && count) count.textContent = group.querySelectorAll('[data-khc-item]').length;
      });
    }

    bind() {
      var self = this;
      var timer;

      if (this.input) {
        this.input.addEventListener('input', function () {
          self.search(self.input.value);
          clearTimeout(timer);
          timer = setTimeout(function () { self.afterSearch(); }, 900);
        });
        this.input.addEventListener('keydown', function (e) {
          if (e.key === 'Escape') self.setQuery('');
          if (e.key === 'Enter') {
            e.preventDefault();
            var first = self.items.find(function (it) { return !it.el.hidden && !it.group.hidden; });
            if (first) self.openItem(first, true);
          }
        });
      }

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', function () {
          self.setQuery('');
          self.input.focus();
        });
      }

      this.chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          self.setQuery(chip.getAttribute('data-khc-suggest'));
          self.afterSearch();
        });
      });

      this.navLinks.forEach(function (link) {
        link.addEventListener('click', function () {
          if (self.input && self.input.value) self.setQuery('');
        });
      });

      this.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-khc-vote-value]');
        if (!btn) return;
        var box = btn.closest('[data-khc-vote]');
        var item = btn.closest('[data-khc-item]');
        var helpful = btn.getAttribute('data-khc-vote-value') === 'yes';
        box.classList.add('is-done');
        box.querySelector(helpful ? '[data-khc-vote-thanks]' : '[data-khc-vote-sorry]').hidden = false;
        publish('kopstorm_help_vote', { question: item ? item.getAttribute('data-khc-slug') : '', helpful: helpful });
      });

      window.addEventListener('hashchange', function () { self.openHash(); });

      var opener = this.querySelector('[data-khc-sheet-open]');
      if (opener && this.sheet) {
        opener.addEventListener('click', function () { self.openSheet(opener); });
        this.sheet.addEventListener('click', function (e) {
          if (e.target.closest('[data-khc-sheet-close]')) self.closeSheet();
          else if (e.target.closest('[data-khc-navlink]')) self.closeSheet(true);
        });
        this.sheet.addEventListener('keydown', function (e) {
          if (e.key === 'Escape') self.closeSheet();
        });
      }

      var focusBtn = this.querySelector('[data-khc-focus-search]');
      if (focusBtn && this.input) {
        focusBtn.addEventListener('click', function () {
          var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          self.input.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
          setTimeout(function () { self.input.focus({ preventScroll: true }); }, reduce ? 0 : 350);
        });
      }
    }

    openSheet(opener) {
      this._opener = opener;
      this.sheet.hidden = false;
      document.documentElement.classList.add('khc-locked');
      var active = this.sheet.querySelector('.khc__nav-link.is-active') || this.sheet.querySelector('.khc__nav-link');
      if (active) active.focus({ preventScroll: true });
    }

    closeSheet(fromLink) {
      if (!this.sheet || this.sheet.hidden) return;
      this.sheet.hidden = true;
      document.documentElement.classList.remove('khc-locked');
      if (!fromLink && this._opener) this._opener.focus({ preventScroll: true });
    }

    setQuery(value) {
      if (!this.input) return;
      this.input.value = value;
      this.search(value);
    }

    search(raw) {
      var query = norm(raw);
      var tokens = query ? query.split(' ').filter(function (t) { return t.length > 0; }).map(stem) : [];
      var searching = tokens.length > 0;
      var matches = 0;
      var self = this;

      this.classList.toggle('is-searching', searching);
      if (this.clearBtn) this.clearBtn.hidden = !searching;

      this.items.forEach(function (it) {
        var hit = !searching || tokens.every(function (t) { return it.text.indexOf(t) !== -1; });
        var isPopularCopy = it.group && it.group.hasAttribute('data-khc-popular');
        it.el.hidden = !hit;
        if (it.q) it.q.innerHTML = it.qHtml;
        if (it.a) it.a.innerHTML = it.aHtml;
        if (hit && searching && !isPopularCopy) {
          matches++;
          self.mark(it.q, tokens);
          self.mark(it.a, tokens);
        }
      });

      this.groups.forEach(function (g) {
        if (g.hasAttribute('data-khc-popular')) {
          g.hidden = searching;
          return;
        }
        g.hidden = searching && !g.querySelector('[data-khc-item]:not([hidden])');
      });

      this.chips.forEach(function (chip) {
        chip.setAttribute('aria-pressed', norm(chip.getAttribute('data-khc-suggest')) === query ? 'true' : 'false');
      });

      if (this.empty) this.empty.hidden = !searching || matches > 0;
      if (this.emptyMail) {
        var mail = this.emptyMail.getAttribute('data-mail');
        var mailA = this.emptyMail.tagName === 'A' ? this.emptyMail : this.emptyMail.querySelector('a');
        if (mailA) mailA.href = 'mailto:' + mail + (searching ? '?subject=' + encodeURIComponent('Question: ' + raw.trim()) : '');
      }

      if (this.status) {
        if (searching && matches > 0) {
          var label = matches === 1 ? this.dataset.labelOne : this.dataset.labelMany;
          this.status.textContent = (label || '[count]').replace('[count]', matches).replace('[query]', raw.trim());
          this.status.hidden = false;
        } else {
          this.status.hidden = true;
          this.status.textContent = '';
        }
      }

      this._last = { query: raw.trim(), results: matches };
    }

    afterSearch() {
      var last = this._last;
      if (!last) return;
      try {
        var url = new URL(window.location.href);
        if (last.query) url.searchParams.set('q', last.query);
        else url.searchParams.delete('q');
        url.hash = '';
        history.replaceState(history.state, '', url.toString());
      } catch (e) { /* ignore */ }
      if (last.query.length >= 3 && last.query !== this._published) {
        this._published = last.query;
        publish('kopstorm_help_search', last);
      }
    }

    mark(root, tokens) {
      if (!root || !tokens.length) return;
      var pattern = new RegExp('(' + tokens.map(escapeRe).join('|') + ')', 'gi');
      var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
      var nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach(function (node) {
        var text = node.nodeValue;
        pattern.lastIndex = 0;
        if (!pattern.test(text)) return;
        pattern.lastIndex = 0;
        var frag = document.createDocumentFragment();
        var last = 0;
        text.replace(pattern, function (match, _g, offset) {
          if (offset > last) frag.appendChild(document.createTextNode(text.slice(last, offset)));
          var m = document.createElement('mark');
          m.className = 'khc__mark';
          m.textContent = match;
          frag.appendChild(m);
          last = offset + match.length;
          return match;
        });
        if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
        node.parentNode.replaceChild(frag, node);
      });
    }

    openItem(it, scroll) {
      if (!it || !it.details) return;
      this.items.forEach(function (x) { x.el.classList.remove('is-target'); });
      it.el.classList.add('is-target');
      it.details.open = true;
      if (!it.details.hasAttribute('open')) it.details.setAttribute('open', '');
      if (scroll) {
        var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        it.el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      }
    }

    openHash() {
      var hash = decodeURIComponent((window.location.hash || '').slice(1));
      if (!hash) return;
      var target = this.items.find(function (it) { return it.el.id === hash; });
      if (!target && hash.indexOf('q-') !== 0) {
        target = this.items.find(function (it) { return it.el.id === 'q-' + hash; });
      }
      if (target) {
        var self = this;
        requestAnimationFrame(function () { self.openItem(target, true); });
      }
    }

    readUrl() {
      var params = new URLSearchParams(window.location.search);
      var q = params.get('q');
      if (q && this.input) {
        this.setQuery(q);
        this._published = q.trim();
      }
      this.openHash();
    }

    spy() {
      if (!('IntersectionObserver' in window) || !this.navLinks.length) return;
      var self = this;
      var visible = new Map();
      this.observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { visible.set(entry.target.id, entry.isIntersecting); });
        var current = null;
        self.groups.some(function (g) {
          if (!g.hidden && visible.get(g.id)) { current = g.id; return true; }
          return false;
        });
        if (!current) return;
        if (self.current) {
          var src = self.navLinks.find(function (l) { return l.getAttribute('data-khc-navlink') === current; });
          var label = src && src.querySelector('span');
          self.current.textContent = label ? label.textContent : self.currentDefault;
        }
        self.navLinks.forEach(function (link) {
          var active = link.getAttribute('data-khc-navlink') === current;
          link.classList.toggle('is-active', active);
          if (active) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        });
      }, { rootMargin: '-20% 0px -60% 0px' });
      this.groups.forEach(function (g) { if (g.id) self.observer.observe(g); });
    }
  }

  customElements.define('kopstorm-help-center', KopstormHelpCenter);
})();
